import Vue from 'vue';

const iconContext = require.context('../assets/icons-new', false, /\.svg$/);
const iconMarkupCache = new Map();
const iconMarkupPromiseCache = new Map();

const sanitizeSvg = (markup) => markup
  .replace(/<\?xml[\s\S]*?\?>/g, '')
  .replace(/<!DOCTYPE[\s\S]*?>/gi, '')
  .trim();

const isInlineSvgMarkup = (value) => typeof value === 'string' && value.includes('<svg');

const resolveIconSource = (moduleValue) => {
  if (!moduleValue) {
    return '';
  }

  if (typeof moduleValue === 'string') {
    return moduleValue;
  }

  if (typeof moduleValue.default === 'string') {
    return moduleValue.default;
  }

  return '';
};

const loadIconMarkup = async (iconName, iconSource) => {
  if (iconMarkupCache.has(iconName)) {
    return iconMarkupCache.get(iconName);
  }

  if (iconMarkupPromiseCache.has(iconName)) {
    return iconMarkupPromiseCache.get(iconName);
  }

  const promise = (async () => {
    if (!iconSource) {
      return '';
    }

    if (isInlineSvgMarkup(iconSource)) {
      const sanitized = sanitizeSvg(iconSource);
      iconMarkupCache.set(iconName, sanitized);
      return sanitized;
    }

    try {
      const response = await fetch(iconSource, { credentials: 'same-origin' });
      const text = await response.text();
      const sanitized = sanitizeSvg(text);
      iconMarkupCache.set(iconName, sanitized);
      return sanitized;
    } catch (error) {
      console.error(`Failed to load icon "${iconName}" from ${iconSource}`, error);
      return '';
    }
  })();

  iconMarkupPromiseCache.set(iconName, promise);
  const markup = await promise;
  iconMarkupPromiseCache.delete(iconName);
  return markup;
};

const createIconComponent = (iconSource, iconName) => ({
  name: `ma-${iconName}`,
  inheritAttrs: false,
  data() {
    return {
      svgMarkup: iconMarkupCache.get(iconName) || '',
    };
  },
  async created() {
    if (!this.svgMarkup) {
      this.svgMarkup = await loadIconMarkup(iconName, iconSource);
    }
  },
  render(h) {
    const attrs = {
      ...this.$attrs,
      'data-ma-icon': iconName,
    };

    return h('span', {
      class: ['ma-icon', `ma-icon--${iconName}`],
      attrs,
      on: this.$listeners,
      domProps: {
        innerHTML: this.svgMarkup || '',
      },
    });
  },
});

const getIconName = (filePath) => filePath.replace('./', '').replace(/\.svg$/, '');

export const registerMaIcons = () => {
  iconContext.keys().forEach((filePath) => {
    const iconName = getIconName(filePath);
    const iconSource = resolveIconSource(iconContext(filePath));
    Vue.component(`ma-${iconName}`, createIconComponent(iconSource, iconName));
  });
};
